<script setup>
  import {nextTick, onBeforeMount, ref, watch} from "vue";

  import {arrow, computePosition, flip, offset, shift} from "@floating-ui/vue";

  defineOptions({
    name: "ComponentTooltip",
  });

  const props = defineProps({
    placement: {
      type: String,
      default: "top",
      // top, top-start, top-end
      // right, right-start, right-end
      // bottom, bottom-start, bottom-end
      // left, left-start, left-end
    },
    tooltip: {
      type: String,
      default: null,
    },
    /**
     * do not show tooltip at all
     */
    forceHidden: {
      type: Boolean,
      default: false,
    },
    forceInline: {
      type: Boolean,
      default: true,
    },
    customClass: {
      type: String,
      default: null,
    },
    customClassLabel: {
      type: String,
      default: "",
    },
    holdOnMouseover: {
      type: Boolean,
      default: false,
    },
    delay: {
      type: Number,
      default: 700,
    },
  });

  const refArrowElement = ref(null);
  const refFloatingElement = ref(null);
  const refTriggerElement = ref(null);

  const id = ref(null);
  const timeout = ref(null);
  const internalForceHidden = ref(props.forceHidden);

  watch(
    () => props.forceHidden,
    (newValue) => {
      internalForceHidden.value = newValue;
    },
  );

  const calculate = () => {
    computePosition(refTriggerElement.value, refFloatingElement.value, {
      placement: props.placement, // << preferred placement
      middleware: [offset(12), flip({}), shift({padding: 10}), arrow({element: refArrowElement})],
    }).then(({middlewareData, placement, x, y}) => {
      Object.assign(refFloatingElement.value.style, {
        left: `${x}px`,
        top: `${y}px`,
      });

      // "placement" is the actual placement regarding flip
      const side = placement.split("-")[0];

      const staticSide = {
        top: "bottom",
        right: "left",
        bottom: "top",
        left: "right",
      }[side];

      if (middlewareData.arrow) {
        Object.assign(refArrowElement.value.style, {
          left: middlewareData?.arrow?.x == null ? "" : `${middlewareData?.arrow.x}px`,
          top: middlewareData?.arrow?.y == null ? "" : `${middlewareData?.arrow.y}px`,
          [staticSide]: `${-refArrowElement.value.offsetWidth / 2}px`,
        });
      }
    });
  };

  onBeforeMount(() => {
    id.value = Math.random().toString(36).slice(4);
  });

  const mouseEnter = () => {
    if (timeout.value) {
      clearTimeout(timeout.value);
    }

    if (internalForceHidden.value) {
      return;
    }

    timeout.value = setTimeout(() => {
      if (refFloatingElement.value) {
        refFloatingElement.value.showPopover();
        nextTick(() => {
          calculate();
        });
      }
    }, props.delay);
  };

  const mouseLeave = () => {
    if (timeout.value) {
      clearTimeout(timeout.value);
    }

    if (internalForceHidden.value) {
      return;
    }

    timeout.value = setTimeout(() => {
      refFloatingElement.value?.hidePopover();
    }, 400);
  };

  const mouseOver = () => {
    if (props.holdOnMouseover && timeout.value) {
      clearTimeout(timeout.value);
    }
  };
</script>

<template>
  <span v-if="tooltip || $slots.tooltip">
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -- wrapper around arbitrary slot content, deliberately without a role; the real control is whatever is slotted and the tooltip is linked via aria-describedby -->
    <span
      ref="refTriggerElement"
      :class="[{'inline-block w-full': !forceInline}, customClassLabel]"
      :aria-describedby="`tooltip-${id}`"
      @mouseenter="mouseEnter()"
      @mouseleave="mouseLeave()"
      @focusin="mouseEnter()"
      @focusout="mouseLeave()"
    >
      <slot />
    </span>

    <span
      v-if="!internalForceHidden"
      :id="`tooltip-${id}`"
      ref="refFloatingElement"
      class="absolute top-0 left-0 m-0 w-max max-w-sm overflow-visible rounded-md p-0 shadow-sm"
      role="tooltip"
      popover
    >
      <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events, vuejs-accessibility/no-static-element-interactions -- pointer-only affordance that keeps the tooltip open while hovering it; the content is exposed to assistive tech via aria-describedby on the trigger -->
      <span
        class="relative z-10 block max-h-96 overflow-y-auto rounded-md bg-gray-500 px-2 py-1 font-medium whitespace-normal text-white"
        :class="customClass"
        :data-test="getTestId('tooltip')"
        @mouseover="mouseOver()"
        @mouseleave="mouseLeave()"
      >
        <slot name="tooltip">
          <div v-text="tooltip" />
        </slot>
      </span>

      <span ref="refArrowElement" class="absolute z-0 size-2 rotate-45 bg-gray-500" :class="customClass" />
    </span>
  </span>
  <span v-else>
    <slot />
  </span>
</template>
